/** Prioridade padrão da fila de volumetria. */
export const VOLUMETRIA_B2C_PRIORIDADE_PADRAO = 2

function pickField(src, ...keys) {
  if (!src || typeof src !== 'object') return undefined
  for (const key of keys) {
    if (src[key] != null && src[key] !== '') return src[key]
  }
  return undefined
}

/**
 * Normaliza GET /volumetria-b2c/cota-diaria (objeto `cota` + campos na raiz).
 */
export function normalizeCotaDiaria(data) {
  const nested = data?.cota && typeof data.cota === 'object' ? data.cota : null
  const src = nested ? { ...data, ...nested } : data || {}

  const limiteDiario = Number(pickField(src, 'LimiteDiario', 'limiteDiario')) || 0
  const usadasHoje = Number(pickField(src, 'UsadasHoje', 'usadasHoje')) || 0
  let restantesHoje = pickField(src, 'RestantesHoje', 'restantesHoje')
  if (restantesHoje == null || !Number.isFinite(Number(restantesHoje))) {
    restantesHoje = Math.max(0, limiteDiario - usadasHoje)
  } else {
    restantesHoje = Number(restantesHoje)
  }

  const tipoRaw = String(pickField(src, 'TipoCobranca', 'tipoCobranca') || 'COTA_DISPONIVEL').toUpperCase()
  let tipoCobranca = 'COTA_DISPONIVEL'
  if (tipoRaw === 'USAR_SALDO' || tipoRaw.includes('SALDO')) tipoCobranca = 'USAR_SALDO'
  else if (tipoRaw === 'BLOQUEADO' || tipoRaw.includes('BLOQUE')) tipoCobranca = 'BLOQUEADO'

  const podeProsseguirRaw = pickField(src, 'PodeProsseguir', 'podeProsseguir')
  const podeProsseguir =
    podeProsseguirRaw === true
    || podeProsseguirRaw === 1
    || podeProsseguirRaw === '1'
    || podeProsseguirRaw === 'true'

  const saldoAtual = Number(pickField(src, 'SaldoAtual', 'saldoAtual')) || 0
  const valorConsultaExtra = Number(
    pickField(src, 'ValorConsultaExtra', 'valorConsultaExtra'),
  ) || 0
  const podeUsarSaldoRaw = pickField(src, 'PodeUsarSaldo', 'podeUsarSaldo')
  const podeUsarSaldo =
    podeUsarSaldoRaw === true
    || podeUsarSaldoRaw === 1
    || podeUsarSaldoRaw === '1'
    || podeUsarSaldoRaw === 'true'
    || tipoCobranca === 'USAR_SALDO'

  const resolvedPodeProsseguir = (() => {
    if (tipoCobranca === 'BLOQUEADO') return false
    if (podeProsseguirRaw == null) return true
    return podeProsseguir
  })()

  return {
    limiteDiario,
    usadasHoje,
    restantesHoje,
    saldoAtual,
    valorConsultaExtra,
    podeUsarSaldo,
    tipoCobranca,
    podeProsseguir: resolvedPodeProsseguir,
    mensagemModal: String(pickField(src, 'MensagemModal', 'mensagemModal') || ''),
  }
}

export function mapSexoToGeneroApi(sexo) {
  const value = String(sexo || '').toLowerCase().trim()
  if (value === 'masculino') return 'M'
  if (value === 'feminino') return 'F'
  return undefined
}

function asOptionalNumber(value) {
  if (value == null || value === '') return undefined
  const n = Number(value)
  return Number.isFinite(n) ? n : undefined
}

/**
 * Monta body para POST /volumetria-b2c/enfileirar a partir dos filtros do wizard Ligdata.
 */
export function buildEnfileirarVolumetriaBody({ nomeConsulta, filters }) {
  const name = String(nomeConsulta || '').trim()
  if (!name) {
    throw new Error('Informe o nome da consulta antes de consultar o volume.')
  }

  const f = filters || {}
  const codigos = (Array.isArray(f.cidades) ? f.cidades : [])
    .map(city => city.codigoIBGE || city.codigoIbge || city.id)
    .filter(v => v != null && String(v).trim() !== '' && !String(v).includes('-'))
    .map(v => Number(v))
    .filter(n => Number.isFinite(n))

  const bairroNomes = (Array.isArray(f.bairros) ? f.bairros : [])
    .map(b => (typeof b === 'object' ? b.name || b.nome : b))
    .filter(Boolean)

  const cbos = (Array.isArray(f.cbos) ? f.cbos : [])
    .map(c => (typeof c === 'object' ? c.code : c))
    .filter(Boolean)
    .map(String)

  const cbosExcluidos = (Array.isArray(f.cbosExcluidos) ? f.cbosExcluidos : [])
    .map(c => (typeof c === 'object' ? c.code : c))
    .filter(Boolean)
    .map(String)

  const canais = (Array.isArray(f.canais) ? f.canais : []).map(c => String(c).toLowerCase())

  const body = {
    nomeConsulta: name,
    prioridade: VOLUMETRIA_B2C_PRIORIDADE_PADRAO,
    genero: mapSexoToGeneroApi(f.genero),
    idadeMin: asOptionalNumber(f.idadeMin),
    idadeMax: asOptionalNumber(f.idadeMax),
    rendaMin: f.rendaPreset === 'qualquer' ? undefined : asOptionalNumber(f.rendaMin),
    rendaMax: f.rendaPreset === 'qualquer' ? undefined : asOptionalNumber(f.rendaMax),
    ufs: Array.isArray(f.ufs) && f.ufs.length ? f.ufs : undefined,
    codigosIBGE: codigos.length ? codigos : undefined,
    bairro: bairroNomes.length ? bairroNomes.join(', ') : undefined,
    filtrarCelular: canais.includes('celular') ? 1 : 0,
    filtrarWhatsApp: canais.includes('whatsapp') ? 1 : 0,
    filtrarEmail: canais.includes('email') ? 1 : 0,
    filtrarFixo: 0,
    excluirNaoPerturbe: 0,
    cbos: cbos.length ? cbos : undefined,
    cbosExcluidos: cbosExcluidos.length ? cbosExcluidos : undefined,
  }

  Object.keys(body).forEach(key => {
    if (body[key] === undefined) delete body[key]
  })

  return body
}

/**
 * Filtros parciais em snake_case para POST personalizadas.
 * Retorna null se não houver filtros úteis (perfil direto só com descrição).
 */
export function buildFiltrosPreenchidosObject(filters) {
  if (!filters || typeof filters !== 'object') return null

  let partial
  try {
    partial = buildEnfileirarVolumetriaBody({
      nomeConsulta: '_',
      filters,
    })
  } catch {
    return null
  }

  const out = {}
  if (partial.genero) out.genero = partial.genero
  if (partial.idadeMin != null) out.idade_min = partial.idadeMin
  if (partial.idadeMax != null) out.idade_max = partial.idadeMax
  if (partial.ufs) out.ufs = Array.isArray(partial.ufs) ? partial.ufs.join(',') : partial.ufs
  if (partial.codigosIBGE) {
    out.codigos_ibge = Array.isArray(partial.codigosIBGE)
      ? partial.codigosIBGE.join(',')
      : partial.codigosIBGE
  }
  if (partial.bairro) out.bairro = partial.bairro
  if (partial.cbos) {
    out.cbos = Array.isArray(partial.cbos) ? partial.cbos.join(',') : partial.cbos
  }
  if (partial.cbosExcluidos) {
    out.cbos_excluidos = Array.isArray(partial.cbosExcluidos)
      ? partial.cbosExcluidos
      : [partial.cbosExcluidos]
  }
  if (partial.rendaMin != null) out.renda_min = partial.rendaMin
  if (partial.rendaMax != null) out.renda_max = partial.rendaMax
  if (partial.filtrarCelular) out.filtrar_celular = partial.filtrarCelular
  if (partial.filtrarWhatsApp) out.filtrar_whatsapp = partial.filtrarWhatsApp
  if (partial.filtrarEmail) out.filtrar_email = partial.filtrarEmail
  if (partial.filtrarFixo) out.filtrar_fixo = partial.filtrarFixo

  return Object.keys(out).length > 0 ? out : null
}

/**
 * Body para POST /volumetria-b2c/personalizadas
 */
export function buildPersonalizadaVolumetriaBody({
  nomeConsulta,
  descricaoLivre,
  modoCobranca,
  filters,
}) {
  const name = String(nomeConsulta || '').trim()
  const desc = String(descricaoLivre || '').trim()
  const modo = String(modoCobranca || '').trim()

  if (!name) throw new Error('Informe o nome da consulta.')
  if (!desc) throw new Error('Descreva o perfil desejado.')
  if (!modo) throw new Error('Selecione a forma de cobrança.')

  const body = {
    nomeConsulta: name,
    descricaoLivre: desc,
    modoCobranca: modo,
  }

  const filtrosPreenchidos = buildFiltrosPreenchidosObject(filters)
  if (filtrosPreenchidos) {
    body.filtrosPreenchidos = filtrosPreenchidos
  }

  return body
}

export function normalizeModeloItem(raw) {
  if (!raw || typeof raw !== 'object') return null
  const id = raw.id ?? raw.Id ?? raw.idModelo ?? raw.IdModelo
  if (id == null) return null
  const filtros = raw.filtros ?? raw.Filtros ?? raw.filtrosJSON ?? raw.FiltrosJSON ?? null
  let parsed = filtros
  if (typeof filtros === 'string') {
    try {
      parsed = JSON.parse(filtros)
    } catch {
      parsed = null
    }
  }
  return {
    id,
    nome: String(raw.nome ?? raw.Nome ?? raw.name ?? `Modelo ${id}`).trim(),
    filtros: parsed && typeof parsed === 'object' ? parsed : {},
    raw,
  }
}

export function normalizeBlacklistItem(raw) {
  if (!raw || typeof raw !== 'object') return null

  let cbosRaw = raw.cbos ?? raw.Cbos
  if (typeof cbosRaw === 'string') {
    try {
      cbosRaw = JSON.parse(cbosRaw)
    } catch {
      cbosRaw = []
    }
  }

  const cbos = (Array.isArray(cbosRaw) ? cbosRaw : [])
    .map(c => ({
      code: String(c.code ?? c.Code ?? c.codigo ?? '').trim(),
      name: c.name ?? c.Name ?? c.nome ?? null,
    }))
    .filter(c => c.code)

  const id = raw.id ?? raw.Id
  if (id == null) return null

  return {
    id,
    name: String(raw.nome ?? raw.Nome ?? raw.name ?? '').trim() || `Blacklist ${id}`,
    cbos,
  }
}

export function normalizeEstado(raw) {
  if (!raw || typeof raw !== 'object') return null
  const uf = String(raw.uf ?? raw.UF ?? raw.sigla ?? raw.Sigla ?? '').trim().toUpperCase()
  const id = raw.id ?? raw.Id ?? raw.estadoId ?? raw.EstadoId ?? raw.idEstado ?? raw.IdEstado
  if (!uf || id == null) return null
  return { id, uf, nome: raw.nome ?? raw.Nome ?? uf }
}

export function normalizeCidade(raw, uf) {
  const name = raw.nome || raw.name || raw.cidade || raw.Cidade || raw.Nome || String(raw)
  const codigoIBGE = raw.codigoIBGE || raw.CodigoIBGE || raw.codigoIbge || raw.idIBGE || raw.IdIBGE || null
  const id = codigoIBGE || raw.id || `${uf}-${name}`
  return {
    id: String(id),
    label: `${name} - ${uf}`,
    name,
    uf,
    codigoIBGE: codigoIBGE != null ? Number(codigoIBGE) || codigoIBGE : null,
  }
}

/**
 * Normaliza retorno de GET /getCbos (CodCbo, CBO, Familia)
 * ou lista de profissões da volumetria v2.
 */
export function normalizeCboApiRows(apiData) {
  const list = Array.isArray(apiData)
    ? apiData
    : (Array.isArray(apiData?.results)
      ? apiData.results
      : (Array.isArray(apiData?.profissoes) ? apiData.profissoes : []))

  if (!Array.isArray(list)) return []

  return list
    .map(r => {
      if (!r || typeof r !== 'object') return null

      const code = String(
        r.CodCbo ?? r.codCbo ?? r.codigo ?? r.Codigo ?? r.code ?? r.cbo ?? r.id ?? '',
      ).replace(/\D/g, '').trim()

      // Em TbCBO o campo `CBO` é o nome da ocupação (não o código).
      const name = String(
        r.CBO ?? r.Nome ?? r.nome ?? r.name ?? r.descricao ?? r.Descricao ?? '',
      ).trim()

      const familia = String(r.Familia ?? r.familia ?? r.group ?? '').trim()
      const familyCode = code.slice(0, 4) || '—'
      const group = familia || familyCode

      if (!code || code.length < 4 || !name) return null
      if (code.toLowerCase() === 'todos' || name.toLowerCase() === 'todos') return null

      return { code, name, group, familyCode }
    })
    .filter(Boolean)
}

/**
 * Agrupa lista plana de CBOs/profissões em famílias para o modal de busca.
 * Aceita o formato getCbos e o da volumetria v2.
 */
export function normalizeProfissoesToCatalog(profissoes) {
  const rows = normalizeCboApiRows(
    Array.isArray(profissoes) ? profissoes : (profissoes || []),
  )
  const byFamily = new Map()

  rows.forEach(item => {
    const familyCode = item.familyCode || String(item.code).slice(0, 4)
    if (!byFamily.has(familyCode)) {
      byFamily.set(familyCode, {
        familyCode,
        familyName: item.group && item.group !== familyCode
          ? item.group
          : (item.name || `Família ${familyCode}`),
        occupations: [],
      })
    }

    const family = byFamily.get(familyCode)
    if (!family.occupations.some(occ => occ.code === item.code)) {
      family.occupations.push({ code: item.code, name: item.name || item.code })
    }

    // Se a família ainda está com nome genérico e temos um group textual, atualiza.
    if (
      family.familyName === `Família ${familyCode}`
      || family.familyName === family.occupations[0]?.name
    ) {
      if (item.group && item.group !== familyCode) {
        family.familyName = item.group
      }
    }
  })

  return Array.from(byFamily.values())
    .map(family => ({
      ...family,
      occupations: family.occupations.sort((a, b) => (
        (a.name || '').localeCompare(b.name || '', 'pt-BR')
      )),
    }))
    .sort((a, b) => a.familyCode.localeCompare(b.familyCode))
}
